<!--
// app/components/pages/region-world-cup/BracketTablesWorldCup.vue
-->
<script setup lang="ts">
import type { BracketData } from "~/src/world-cup/domain/models/Bracket";
import type { MatchCenterData } from "~/src/match/domain/models/Match";
import { useCompetitionStore } from "~/app/stores/competitionStore";
import { useSeasonFilter } from "~/app/composables/season/seasonFilter";
import BracketWorldCupDatasource2024 from "~/src/world-cup/datasources/WorldCupBracket.json";
import SliderWrapper from "~/app/components/Shared/SliderWrapper.vue";
import SeasonsFilter from "~/app/components/Shared/SeasonsFilter.vue";
import NewBracketWorldCup from "~/app/components/pages/region-world-cup/NewBracketWorldCup.vue";
import BracketWorldCupFirstEdition from "~/app/components/pages/region-world-cup/BracketWorldCupFirstEdition.vue";
import { useSeasons } from "~/app/composables/season/seasons";
import { teamArrToObjById } from "~/src/team/domain/services/teamService";

type Props = {
  isHomeComponent?: boolean;
  inOtherLeaguesHome?: boolean;
  seasonIdData?: number;
};

const props = defineProps<Props>();

const { currentSeasonTeams, currentSeason } = useCompetitionStore();
const { getSeasonTeams, useFetchParamsSeasonMatchCenterData } = useSeasons();
const { seasonId, seasonClusterId } = useSeasonFilter();
const { locale } = useI18n();

const teamsObj = ref({});
const bracketData = ref<BracketData | MatchCenterData[]>();
const bracketKey = ref(0);

const sliderWrapperRef = ref<InstanceType<typeof SliderWrapper> | null>(null);

const getBracketData = async () => {
  const _seasonId = props.seasonIdData ? props.seasonIdData : seasonId.value;

  await nextTick();

  if (currentSeason.id === _seasonId) {
    teamsObj.value = await teamArrToObjById(currentSeasonTeams, currentSeason.id);
    bracketData.value = teamsObj.value;
  } else {
    const teams = await getSeasonTeams(_seasonId);
    teamsObj.value = await teamArrToObjById(teams, _seasonId);
  }

  if (_seasonId === 13) {
    bracketData.value = BracketWorldCupDatasource2024;
  } else {
    const { path, opts } = useFetchParamsSeasonMatchCenterData(_seasonId, locale.value);
    const { data, error } = await useFetch(path, opts);

    if (error.value) {
      console.error(error.value);
      bracketData.value = [];
    }

    bracketData.value = data.value ? data.value : [];
  }
  bracketKey.value++;

  // Wait for DOM updates and manually trigger SliderWrapper re-initialization
  await nextTick();
  await nextTick(); // Double nextTick to ensure child components are fully rendered

  // Manually re-initialize the slider after data changes
  if (sliderWrapperRef.value) {
    sliderWrapperRef.value.initializeSlider();
  }
};

await getBracketData();
</script>

<template>
  <div v-if="!isHomeComponent" class="text-right">
    <seasons-filter
      v-model:season-id="seasonId"
      v-model:season-cluster-id="seasonClusterId"
      @change="getBracketData"
    />
  </div>

  <slider-wrapper
    v-if="bracketData"
    ref="sliderWrapperRef"
    :scroll-distance="204"
    :enable-bounce-animation="true"
    auto-scroll-selector=".round-col-container.active-stage:last-of-type"
    scrollable-selector="#tablesSlider"
  >
    <bracket-world-cup-first-edition
      v-if="seasonId === 13 && teamsObj && bracketData"
      :teams-obj="teamsObj"
      :bracket-data="bracketData"
      :bracket-key="bracketKey"
    ></bracket-world-cup-first-edition>
    <new-bracket-world-cup
      v-else
      :is-home-component="isHomeComponent"
      :season-id-data="seasonIdData ? seasonIdData : seasonId"
      :in-other-leagues-home="inOtherLeaguesHome"
      :teams-obj="teamsObj"
      :bracket-data="bracketData"
      :bracket-key="bracketKey"
    ></new-bracket-world-cup>
  </slider-wrapper>
</template>

<style scoped></style>
